import React, { useEffect, useState } from "react";
import { useClickOutside } from "@mantine/hooks";
import cn from "../../lib/utils/cn";

interface MenuItem {
	icon?: React.ReactNode
	label: string
	onClick: () => void
}

interface OptionsMenuProps {
	isOpen: boolean
	onClose: () => void
	items: MenuItem[]
	className?: string
	iconClassName?: string
	labelClassName?: string
	itemClassName?: string
	directionX?: "right" | "left"
	directionY?: "top" | "bottom"
}

const OptionsMenu: React.FC<OptionsMenuProps> = ({
	isOpen,
	onClose,
	items,
	className,
	iconClassName,
	labelClassName,
	itemClassName,
	directionX = "right",
	directionY = "top"
}) => {
	const [bottomOffset, setBottomOffset] = useState<number>(0);

	const ref = useClickOutside(() => {
		onClose();
		setBottomOffset(0);
	});

	useEffect(() => {
		if (!isOpen) return;

		const handleResize = () => {
			if (ref.current) {
				const rect = ref.current.getBoundingClientRect();
				const windowHeight = window.innerHeight;
				setBottomOffset(windowHeight - rect.bottom);
			}
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => window.removeEventListener("resize", handleResize);
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			ref={ref}
			className={cn(
				"absolute bg-white dark:bg-background-dark default-shadow  rounded-lg z-[1001]  border border-stroke-light dark:border-stroke-light-dark  overflow-hidden",
				directionX === "right" ? "left-3/4" : "right-3/4",
				directionY === "top" || bottomOffset < 150 ? "bottom-2" : "top-2",
				className
			)}
		>
			{items.map(({ icon, label, onClick }, index) => (
				<div
					key={index}
					className={cn(
						"flex items-center gap-2 cursor-pointer px-4 rounded hover:bg-gray-100 h-11 w-40",
						itemClassName
					)}
					onClick={(e) => {
						e?.stopPropagation();
						onClick();
						onClose();
					}}
				>
					{!!icon && (
						<span
							className={cn(
								"w-5 h-5 text-text-secondary flex items-center",
								iconClassName ?? labelClassName
							)}
						>
							{icon}
						</span>
					)}
					<span
						className={cn("text-text-main flex items-center", labelClassName)}
					>
						{label}
					</span>
				</div>
			))}
		</div>
	);
};

export default OptionsMenu;
